import {
  CellClickedEvent,
  CellMouseOverEvent,
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
} from '@ag-grid-community/core';
import { attr, customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { Connect } from '@genesislcap/foundation-comms';
import { Modal } from '@genesislcap/foundation-ui';
import { dealSeriesStyles as styles } from './deal-series.styles';
import { dealSeriesTemplate as template } from './deal-series.template';
import {
  BadgeRenderer,
  ClearAllHeaderRenderer,
  ConditionsRenderer, IconCellRenderer,
  MaturityAmountRenderer
} from '../cell-renderers';
import { MaturityOrder, OrderStatusEnum } from './deal-series.types';
import {
  conditionsTooltipFormatter,
  getClearAllMessages,
  getTotalsRowData,
  formatOrderQuantityForCell,
  getOrderAmountCap,
  hasOrderAmountApplied,
  hasZeroCouponPrincipalAmount,
  isFullParOrderAmountApplied,
  isSplitGlobalAllApplied,
  isZeroCouponMaturity,
  shouldHighlightPrincipalAmountOnHover,
  hasOrderConditions,
  isDraftOrder,
  isOrderInTransit,
  mapMaturityOrderForDeletion,
  mapMaturityOrderForSubmission,
  orderAmountFormatter,
  parseNumberAmount,
  resolveManualOrderQuantityEdit,
  getOrderPatchAfterAllClear,
  shouldGlobalAllClear,
  isSubmittedFullParCapOnRecord,
  hasSubmittedAmountOnRecord,
  getSubmittedAmountRevertPatch,
  shouldDeleteDraftOrder,
  validateAmount,
  formatToThreeDecimals,
  shouldShowPutDateColumn,
  cappedGridHeightPx,
  gridFitsViewport,
  getGridViewportBottomPx,
  gridWouldOverflowViewport,
} from './deal-series.util';
import {
  getQuantityColumnValidationColDef,
} from './deal-series.quantity-validation';
import {
  hasAnyPriceChangeFlag,
  getStatusIconConfig,
  isOrderEditableForDealStatus,
  isOrderModifiable,
  isOrderReconfirmed,
  isOrderEligibleForReconfirm,
  isOrderInstructedForReconfirm,
  isReconfirmResetTarget,
  isReconfirmRowResetEnabled,
  resolveSplitRowReconfirmEligible,
  getOrdersEligibleForGlobalAll,
  getQuantityCellValidationState,
} from '../deal-detail/deal-reconfirm.utils';
import { BondSeries } from '../deal-detail/deal-detail.types';
import { getFixedWidthConfig } from '../deals.util';
import { createPriceChangeCellRenderer, pinnedAwareFormatter, refreshPriceChangeColumns } from '../deal-detail/reprice-window-grid.utils';
import {
  conditionsSummaryColDef,
  couponColDef,
  createNotesColDef,
  DEAL_SERIES_COLUMN_WIDTHS,
  dealSeriesBorder,
  lightGreyCell,
  maturityAmountColDef,
  maturityDateColDef,
  putDateColDef,
  priceColDef,
  getEconomicsColumnDefs,
  statusColDef,
  yieldColDef,
} from './deal-series.col-defs';
import { QuantityHeaderRenderer } from '../cell-renderers/quantity-header.renderer';
import { notifyApiError, notifyWarning } from '../../../utils/utils';
import { WARNING_MESSAGES } from './deal-series.constants';
import { notifyOrderBlocked, OrderBlockReason } from './deal-orders-notifications';
import { CriteriaBuilder, ExpressionBuilder } from '@genesislcap/foundation-criteria';
import { TaxStatusEnum } from '../deals.types';
import { isRepriceWindow } from './deal-status';
import { computeSinkingFundPanelLayout, isTermBondMaturity } from './sinking-fund-schedule.util';
import { SplitOrderModal } from './split-order-modal/split-order-modal';
import { SplitOrderRow } from './split-order-modal/split-order-modal.types';
import {
  buildCollapsedLoneOrderFromSplitChild,
  buildGridRowAfterSplitChildrenCleared,
  buildSplitParentRollupFromChildren,
  fetchBondOrderChildren,
  getSplitOrderSaveState,
  getSplitParentQuantityTotal,
  getSplitConditionsRollupOrder,
  isSplitChildOrder,
  isSplitMaturityGridOrder,
  isSplitRowRemovable,
  getSplitRowsForSave,
  getSplitRowsForAllClearSave,
  mergeChildWithGridParent,
  resolveClearAllDraftBuckets,
  resolveSplitParentRollupsForGrid,
  coerceSplitParentDisplayStatusDuringTransitHold,
} from './split-order.util';

@customElement({
  name: 'deal-series',
  template,
  styles,
})
export class DealSeries extends GenesisElement {
  @observable conditionsActive: boolean;

  @Connect connect: Connect;

  @observable conditionsPopup: HTMLElement;

  @observable series: BondSeries;

  @observable bondSeriesList: BondSeries[];

  @attr({ attribute: 'call-feature-description' }) callFeatureDescription: string;

  @attr({ attribute: 'deal-status' }) dealStatus: string;

  @observable totalDealQuantity: number = 0;

  /** From parent: deal has at least one draft + reconfirm-eligible order (any series). When true, do not show "Maturity not impacted" warning. */
  @attr({ attribute: 'deal-has-eligible-orders-to-reset', mode: 'boolean' }) dealHasEligibleOrdersToReset: boolean;

  /** From parent: order entry is outside the scheduled window (before begin or after end). Blocks all order editing and submission. */
  @attr({ attribute: 'order-period-expired', mode: 'boolean' }) orderPeriodExpired: boolean;

  totalDealQuantityChanged(): void {
    this.updateTotalsRow();
  }

  dealStatusChanged(): void {
    if (!this.gridApi) return;
    this.gridOptions.context = { dealStatus: this.dealStatus };
    refreshPriceChangeColumns(this.gridApi);
    this.gridApi.refreshCells({ force: true });
    this.gridApi.redrawRows();
  }

  private quantityValidationState(order: MaturityOrder | null | undefined) {
    return getQuantityCellValidationState(order, this.dealStatus);
  }

  orderPeriodExpiredChanged(): void {
    if (!this.gridApi) return;
    this.gridApi.refreshCells({ force: true });
  }

  @observable orderEditConditions: MaturityOrder;

  @observable sinkingFundModalVisible = false;

  @observable sinkingFundScheduleJson: string | null = null;

  @observable sinkingFundPanelStyle = '';

  @observable sinkingFundPanelHovered = false;

  private sinkingFundHideTimeout: number | null = null;

  @observable principalAmountModalVisible = false;

  @observable principalAmountValue: string | null = null;

  @observable principalAmountHighlighted = false;

  @observable principalAmountAnchorTop = 0;

  @observable principalAmountAnchorLeft = 0;

  @observable gridOptions: GridOptions;

  @observable flexMaturityOptions: MaturityOrder[] = [];

  // — Clear-all / Apply-all modal copy
  @observable clearAllModalHeader: string = 'Clear All Draft Orders';
  @observable clearAllModalBody: string = 'Are you sure you want to clear all draft orders?';
  @observable clearAllModalBodySecondLine: string = 'Orders In Transit will not be cleared.';
  @observable clearAllModalButtonText: string = 'Yes, Clear';

  private changedRowIds: Set<string> = new Set(); // — selective redraw
  private lastOrderStatusByMaturityId: Map<string, OrderStatusEnum | undefined> = new Map();
  private splitParentRollupRefreshInFlight = false;
  private splitParentStatusPollInterval: ReturnType<typeof setInterval> | null = null;
  private splitParentHasInTransitChildren = false;
  private splitParentPollGraceUntil = 0;
  private splitParentRollupRefreshDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly SPLIT_PARENT_STATUS_POLL_MS = 5_000;
  private static readonly SPLIT_PARENT_POLL_GRACE_MS = 120_000;
  private static readonly SPLIT_PARENT_ROLLUP_REFRESH_DEBOUNCE_MS = 150;

  private isInRepriceWindow(): boolean {
    return isRepriceWindow(this.dealStatus);
  }

  /**
   * Show failure toasts when async order processing returns technical-issue statuses:
   * - PENDING -> SUBMITTED_*_FAILURE OR DRAFT/SUBMITTED_AND_DRAFT with errorMessage means submit failed
   * - PENDING_CANCELLED -> SUBMITTED_*_FAILURE OR DRAFT/SUBMITTED_AND_DRAFT with errorMessage means cancel failed
   */
  private notifyOrderFailureTransitions(orders: MaturityOrder[]): void {
    let submitFailed = false;
    let cancelFailed = false;

    for (const order of orders) {
      const maturityId = order.MATURITY_ID;
      const currentStatus = order.ORDER_STATUS;
      const previousStatus = this.lastOrderStatusByMaturityId.get(maturityId);
      const isTechnicalFailure =
        currentStatus === OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE ||
        currentStatus === OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE;
      const isRevertedWithError =
        (currentStatus === OrderStatusEnum.DRAFT || currentStatus === OrderStatusEnum.SUBMITTED_AND_DRAFT) &&
        !!order.ERROR_MESSAGE;
      const isCancelRollbackToPreviousWithError =
        previousStatus === OrderStatusEnum.PENDING_CANCELLED &&
        currentStatus != null &&
        currentStatus !== OrderStatusEnum.PENDING_CANCELLED &&
        !!order.ERROR_MESSAGE;

      if (isTechnicalFailure || isRevertedWithError || isCancelRollbackToPreviousWithError) {
        if (previousStatus === OrderStatusEnum.PENDING) {
          submitFailed = true;
        } else if (previousStatus === OrderStatusEnum.PENDING_CANCELLED) {
          cancelFailed = true;
        }
      }

      this.lastOrderStatusByMaturityId.set(maturityId, currentStatus);
    }

    if (submitFailed) notifyOrderBlocked(OrderBlockReason.ORDER_SUBMIT_FAILED);
    if (cancelFailed) notifyOrderBlocked(OrderBlockReason.ORDER_CANCEL_FAILED);
  }

  /** — In reprice window: only price-changed orders; otherwise excludes in-transit and reconfirmed. */
  private getModifiableOrders(orders: MaturityOrder[]): MaturityOrder[] {
    return orders.filter((o) => isOrderEditableForDealStatus(o, this.dealStatus));
  }

  /**
   * — Blocks edit if order is in-transit, reconfirmed (with price change), or (in reprice window) order not editable.
   * Deal-status gates apply to submit/cancel only; draft save is not blocked by deal status.
   */
  private blockEditAndNotifyIfNeeded(order: MaturityOrder): boolean {
    if (isOrderInTransit(order)) {
      notifyOrderBlocked(OrderBlockReason.ORDER_IN_TRANSIT);
      return true;
    }
    if (isOrderReconfirmed(order) && hasAnyPriceChangeFlag(order)) {
      notifyOrderBlocked(OrderBlockReason.ORDER_RECONFIRMED);
      return true;
    }
    if (this.isInRepriceWindow()) {
      const editable = this.dealHasEligibleOrdersToReset && isOrderEditableForDealStatus(order, this.dealStatus);
      if (!editable) {
        notifyOrderBlocked(
          hasAnyPriceChangeFlag(order) ? OrderBlockReason.ORDERS_DEAL_STATUS : OrderBlockReason.MATURITY_NOT_IMPACTED_BY_PRICE_CHANGE,
          hasAnyPriceChangeFlag(order) ? { dealStatus: this.dealStatus } : undefined,
        );
        return true;
      }
    }
    return false;
  }

  /**
   * Gates opening the conditions modal (in-transit, reconfirmed, then standard edit blocks).
   */
  private blockConditionsModalOpenAndNotifyIfNeeded(order: MaturityOrder): boolean {
    if (isOrderInTransit(order)) {
      notifyOrderBlocked(OrderBlockReason.ORDER_IN_TRANSIT);
      return true;
    }
    if (isOrderReconfirmed(order) && hasAnyPriceChangeFlag(order)) {
      notifyOrderBlocked(OrderBlockReason.ORDER_RECONFIRMED);
      return true;
    }
    return this.blockEditAndNotifyIfNeeded(order);
  }

  /** Shows appropriate notification when Global All has no eligible orders. */
  private notifyGlobalAllBlocked(orders: MaturityOrder[]): void {
    const allInTransit = orders.every((o) => isOrderInTransit(o));
    if (allInTransit) {
      notifyOrderBlocked(OrderBlockReason.ORDER_IN_TRANSIT);
      return;
    }
    if (this.isInRepriceWindow()) {
      const allNotImpacted = orders.every((o) => !hasAnyPriceChangeFlag(o));
      notifyOrderBlocked(
        allNotImpacted ? OrderBlockReason.MATURITY_NOT_IMPACTED_BY_PRICE_CHANGE : OrderBlockReason.ORDERS_DEAL_STATUS,
        allNotImpacted ? undefined : { dealStatus: this.dealStatus },
      );
      return;
    }
    if (orders.every((o) => isOrderReconfirmed(o) && hasAnyPriceChangeFlag(o))) {
      notifyOrderBlocked(OrderBlockReason.ORDER_RECONFIRMED);
      return;
    }
    notifyOrderBlocked(OrderBlockReason.ORDERS_DEAL_STATUS, { dealStatus: this.dealStatus });
  }

  private setGridOptions(taxStatus: TaxStatusEnum): void {
    const getIsInRepriceWindow = () => this.isInRepriceWindow();
    const quantityValidationColDef = getQuantityColumnValidationColDef({
      getDealStatus: () => this.dealStatus,
      pinnedRowCellStyle: {
        borderLeft: dealSeriesBorder,
        textAlign: 'right',
      },
    });

    const columnDefs: ColDef[] = [maturityDateColDef];
    if (shouldShowPutDateColumn(this.series?.SECURITY_TYPE)) {
      columnDefs.push(putDateColDef);
    }
    columnDefs.push(
      {
        ...maturityAmountColDef,
        cellClassRules: {
          'term-bond-par-amount': (params) =>
            !params.node.isRowPinned() &&
            (isTermBondMaturity(params.data?.SINKING_FUND_SCHEDULE) ||
              isZeroCouponMaturity(params.data?.SECURITY_TYPE)),
        },
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'MATURITY_AMOUNT_CHANGED', (params) => orderAmountFormatter(params.value)),
      },
      {
        ...couponColDef,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'COUPON_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
      {
        ...yieldColDef,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'YIELD_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
    );
    const economicsColumnDefs = getEconomicsColumnDefs(taxStatus);
    columnDefs.push(...economicsColumnDefs.beforePrice);

    columnDefs.push(
      {
        ...priceColDef,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'PRICE_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
      ...economicsColumnDefs.afterPrice,
      createNotesColDef(),
      {
        field: 'QUANTITY',
        headerName: 'Order Amount ($M)',
        headerClass: 'ag-right-aligned-header',
        cellClass: 'focus-border',
        headerComponent: QuantityHeaderRenderer,
        headerComponentParams: {
          editable: true,
          handleAllClicked: (params) => {
            const maturityOrders = this.getAllOrders();
            if (maturityOrders.length === 0) return;
            if (this.isInRepriceWindow() && !this.dealHasEligibleOrdersToReset) {
              notifyOrderBlocked(OrderBlockReason.CLEAR_ALL_REPRICE_NO_RESET);
              return;
            }
            const eligibleForGlobalAll = getOrdersEligibleForGlobalAll(maturityOrders, this.dealStatus);
            if (eligibleForGlobalAll.length === 0) {
              this.notifyGlobalAllBlocked(maturityOrders);
              return;
            }
            if (shouldGlobalAllClear(maturityOrders)) {
              this.toggleAutoResetForAllOrders(maturityOrders, false);
              return;
            }
            this.allOrdersUpdateConfirmModal.show();
          },
        },
        ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.orderAmount),
        width: DEAL_SERIES_COLUMN_WIDTHS.orderAmount,
        onCellClicked: (params) => {
          if (params.node.isRowPinned()) return;
          if (isSplitMaturityGridOrder(params.data)) return;
          if (this.blockEditAndNotifyIfNeeded(params.data)) return;
        },
        ...quantityValidationColDef,
        cellRendererSelector: (params) => {
          if (params.node.isRowPinned()) {
            return undefined;
          }

          return { component: MaturityAmountRenderer };
        },
        cellClassRules: {
          ...quantityValidationColDef.cellClassRules,
          'no-selection': (params) => params.node.isRowPinned(),
          'hover-effect': (params) =>
            params.node.rowPinned === undefined &&
            !isSplitMaturityGridOrder(params.data) &&
            params.data.ORDER_STATUS !== OrderStatusEnum.PENDING &&
            params.data.ORDER_STATUS !== OrderStatusEnum.PENDING_CANCELLED,
        },
        cellRendererParams: (params: ICellRendererParams<MaturityOrder>) => {
          const quantityState = this.quantityValidationState(params.data);
          const isSplitParent = isSplitMaturityGridOrder(params.data);
          const orderAmountDisabled =
            this.isInRepriceWindow()
              ? !this.dealHasEligibleOrdersToReset || !isOrderEligibleForReconfirm(params.data)
              : !isOrderEditableForDealStatus(params.data, this.dealStatus);
          return {
            maxAmount: getOrderAmountCap(params.data),
            orderAmount: formatOrderQuantityForCell(params.data),
            allActive: isSplitParent
              ? isSplitGlobalAllApplied(params.data)
              : isFullParOrderAmountApplied(params.data),
            quantityError: quantityState.hasError,
            disabled: orderAmountDisabled,
            amountReadonly: isSplitParent || orderAmountDisabled,
            showSplitButton: isSplitParent || !orderAmountDisabled,
            splitActive: isSplitParent,
            splitListener: () => this.openSplitOrderModal(params.data),
            changeListener: (value: string) => {
              if (isSplitParent) return;
              const currentOrder = this.gridApi.getRowNode(params.data.MATURITY_ID)?.data as MaturityOrder;
              if (this.blockEditAndNotifyIfNeeded(currentOrder)) return;
              if (validateAmount(value)) {
                const { quantity: quantityValue, isZeroCancelIntent, shouldIgnore } =
                  resolveManualOrderQuantityEdit(currentOrder, value);
                if (shouldIgnore) {
                  this.redrawRow(params.data.MATURITY_ID);
                  return;
                }
                this.setRowEditForField(params.data.MATURITY_ID, {
                  ...this.gridApi.getRowNode(params.data.MATURITY_ID).data,
                  [params.colDef.field]: quantityValue,
                  IS_AUTO_RESET_ORDER_AMOUNT: false,
                  IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
                });
              }
              this.redrawRow(params.data.MATURITY_ID);
            },
            allListener: (all: boolean) => {
              if (isSplitParent) {
                if (this.blockEditAndNotifyIfNeeded(params.data)) return;
                void this.toggleAutoResetForAllOrders(
                  all
                    ? getOrdersEligibleForGlobalAll([params.data], this.dealStatus)
                    : [params.data],
                  all,
                  { scopeToPassedOrders: !all },
                );
                return;
              }
              const rowNode = this.gridApi.getRowNode(params.data.MATURITY_ID);
              const currentOrder = (rowNode?.data ?? params.data) as MaturityOrder;
              if (this.blockEditAndNotifyIfNeeded(currentOrder)) return;
              if (all) {
                if (
                  isSubmittedFullParCapOnRecord(currentOrder) &&
                  isFullParOrderAmountApplied(currentOrder)
                ) {
                  return;
                }
                this.setRowEditForField(params.data.MATURITY_ID, {
                  ...currentOrder,
                  IS_AUTO_RESET_ORDER_AMOUNT: true,
                  QUANTITY: getOrderAmountCap(currentOrder),
                  IS_ZERO_CANCEL_INTENT: false,
                });
              } else {
                if (hasSubmittedAmountOnRecord(currentOrder)) {
                  void this.resetOrdersToLastSubmittedSnapshot([currentOrder]).then((ok) => {
                    if (ok) this.redrawRow(params.data.MATURITY_ID);
                  });
                } else {
                  const patched = {
                    ...currentOrder,
                    ...getOrderPatchAfterAllClear(currentOrder),
                  };
                  this.setRowEditForField(params.data.MATURITY_ID, patched, {
                    isClearingAllAmounts: currentOrder.ORDER_STATUS !== OrderStatusEnum.DRAFT,
                  });
                  this.redrawRow(params.data.MATURITY_ID);
                }
              }
            },
          };
        },
        valueFormatter: (params) => {
          if (params.node.isRowPinned()) {
            const seriesTotal = params.value || 0;
            const fmtSeriesTotal = seriesTotal === 0 ? '0' : orderAmountFormatter(seriesTotal);
            const isSingleSeries = !this.bondSeriesList || this.bondSeriesList.length <= 1;
            if (isSingleSeries) {
              return fmtSeriesTotal;
            }
            const dealTotal = this.totalDealQuantity || 0;
            const fmtDealTotal = dealTotal === 0 ? '0' : orderAmountFormatter(dealTotal);
            return `${fmtSeriesTotal}/ ${fmtDealTotal}`;
          }
          return orderAmountFormatter(params.value);
        },
      },
      {
        ...conditionsSummaryColDef,
        cellClass: 'conditions-summary-cell focus-border',
        onCellClicked: (event: CellClickedEvent<MaturityOrder>) => {
          if (event.node.isRowPinned()) return;
          if (isSplitMaturityGridOrder(event.data)) return;
          if (this.blockConditionsModalOpenAndNotifyIfNeeded(event.data)) return;
          const rowNode = this.gridApi?.getRowNode(event.data.MATURITY_ID);
          const latestOrderData = rowNode?.data || event.data; // — fresh after submit/cancel
          this.orderEditConditions = latestOrderData;
          this.conditionsActive = true;
        },
        cellClassRules: {
          'no-selection': (params) => params.node.isRowPinned(),
          'hover-effect': (params) =>
            params.node.rowPinned === undefined &&
            !isSplitMaturityGridOrder(params.data) &&
            params.data.ORDER_STATUS !== OrderStatusEnum.PENDING &&
            params.data.ORDER_STATUS !== OrderStatusEnum.PENDING_CANCELLED,
        },
        cellStyle: (params) => {
          if (params.node.isRowPinned()) return {};
          return {
            ...lightGreyCell,
            cursor: isSplitMaturityGridOrder(params.data) ? 'default' : 'pointer',
          };
        },
        tooltipValueGetter: (params) =>
          conditionsTooltipFormatter(params.data, this.flexMaturityOptions),
      },
      {
        ...statusColDef,
        cellClass: 'focus-border',
        cellStyle: (params) =>
          params.node.isRowPinned()
            ? {}
            : {
                ...lightGreyCell,
                borderLeft: dealSeriesBorder,
              },
        field: 'ORDER_STATUS',
        headerName: 'Order Status',
        cellRendererParams: (params: ICellRendererParams<MaturityOrder>) =>
          getStatusIconConfig(params, this.dealStatus),
      },
      {
        field: 'actions',
        cellClass: 'focus-border',
        headerName: '',
        ...getFixedWidthConfig(DEAL_SERIES_COLUMN_WIDTHS.actions),
        headerComponent: ClearAllHeaderRenderer,
        headerComponentParams: {
          clearAllHandler: () => {
            this.toggleClearAllModal();
          },
        },
        cellRendererSelector: (params) => {
          if (params.node.isRowPinned()) {
            return undefined;
          }

          return { component: IconCellRenderer };
        },
        cellStyle: (params) => {
          if (params.node.isRowPinned()) {
            return {};
          }
          return {
            borderLeft: dealSeriesBorder,
            borderRight: dealSeriesBorder,
          };
        },
        cellRendererParams: (params: ICellRendererParams) => {
          if (params.node.isRowPinned()) {
            return;
          }

          if (isSplitMaturityGridOrder(params.data)) {
            return undefined;
          }

          const hasNoQuantity = String(params.data.QUANTITY ?? '').trim() === '';
          const hasNoConditions = !hasOrderConditions(params.data);
          const isReset = hasNoQuantity && hasNoConditions;

          // In reprice window: only when at least one order is reconfirm-eligible (global rule), and this order is reconfirm-eligible, allow actions.
          const inRepriceWindow = this.isInRepriceWindow();
          const isReconfirmEligible = resolveSplitRowReconfirmEligible(
            params.data,
            null,
            this.dealStatus,
          );
          const allowAction =
            !inRepriceWindow || (this.dealHasEligibleOrdersToReset && isReconfirmEligible);

          const showSubmittedTrackReset =
            params.data.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT ||
            (inRepriceWindow && isReconfirmResetTarget(params.data, this.dealStatus));

          if (showSubmittedTrackReset) {
            return {
              iconSrc: '/deal-icons/undo.svg',
              enabled: isReconfirmRowResetEnabled(params.data, this.dealStatus),
              clickHandler: () => this.revertOrder(params.data),
            };
          }

          switch (params.data.ORDER_STATUS) {
            case OrderStatusEnum.SUBMITTED_AND_DRAFT:
              return undefined;
            case OrderStatusEnum.DRAFT:
              if (isReset) return undefined; // — hide eraser when already cleared
              return {
                iconSrc: '/deal-icons/eraser.svg',
                enabled: allowAction,
                clickHandler: () => this.revertOrder(params.data),
              };
          }
        },
      }
    );

    this.gridOptions = {
      suppressMovableColumns: true,
      columnDefs,
      headerHeight: 40,
      defaultColDef: {
        filter: true,
        resizable: false,
        editable: false,
        sortable: false,
        suppressMenu: true,
        flex: 0,
        suppressNavigable: (params) => params.node.isRowPinned(),
        cellClassRules: {
          'no-selection': (params) => params.node.isRowPinned(),
        },
      },
      suppressContextMenu: true,
      rowHeight: 40,
      components: {
        badgeRenderer: BadgeRenderer,
        conditionsRenderer: ConditionsRenderer,
      },
      getRowId: (params) => params.data.MATURITY_ID,
      tooltipShowDelay: 0,
      onRowDataUpdated: (params) => {
        this.beginSplitParentTransitHoldIfStreamedFromPending();
        if (this.isSplitParentTransitHoldActive()) {
          this.coerceSplitParentDisplayStatusesDuringTransitHold(params.api);
          this.scheduleSplitParentRollupRefreshFromChildren();
        }
        this.notifyOrderFailureTransitions(this.getAllOrders());
        this.syncSplitParentStatusPolling();
        params.api.refreshHeader();
        this.emitOrdersChanged();
        // — Redraw only changed rows when tracked; else full redraw
        if (this.changedRowIds.size > 0) {
          const nodes = Array.from(this.changedRowIds).map((id) => params.api.getRowNode(id)).filter(Boolean);
          if (nodes.length) params.api.redrawRows({ rowNodes: nodes });
          this.changedRowIds.clear();
        } else {
          params.api.redrawRows();
        }
        // Re-evaluate Order Amount column (disabled state depends on dealHasEligibleOrdersToReset from parent) after parent has updated.
        setTimeout(() => {
          if (params.api) params.api.refreshCells({ force: true });
        }, 0);
        this.updateGridContainerHeight();
      },
      tabToNextCell: (params) => {
        const { previousCellPosition, backwards, api, columnApi } = params;
        const { column, rowIndex } = previousCellPosition;
        const isQuantity = column.getColId() === 'QUANTITY';
        const rowCount = api.getDisplayedRowCount();

        if (isQuantity) {
          const nextRow = backwards ? rowIndex - 1 : rowIndex + 1;
          return nextRow >= 0 && nextRow < rowCount
            ? { rowIndex: nextRow, column, rowPinned: null }
            : null;
        }

        const columns = columnApi.getAllDisplayedColumns();
        const colIndex = columns.indexOf(column);

        let nextCol = backwards ? colIndex - 1 : colIndex + 1;
        let nextRow = rowIndex;

        if (nextCol >= columns.length) {
          nextCol = 0;
          nextRow++;
        } else if (nextCol < 0) {
          nextCol = columns.length - 1;
          nextRow--;
        }

        if (nextRow < 0 || nextRow >= rowCount) return null;

        return {
          rowIndex: nextRow,
          column: columns[nextCol],
          rowPinned: null,
        };
      },
      onCellMouseOver: (event) => {
        if (event.column?.getColId() !== 'MATURITY_AMOUNT') {
          return;
        }
        this.handleSinkingFundCellMouseOver(event);
      },
      onCellMouseOut: (event) => {
        if (event.column?.getColId() !== 'MATURITY_AMOUNT') {
          return;
        }
        this.handleSinkingFundCellMouseOut();
      },
      onCellFocused: (event) => {
        const colId = event.column['colId'];
        const rowIndex = event.rowIndex;
        if (colId !== 'QUANTITY' || rowIndex == null) return;
        const rowNode = event.api.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode) return;
        // — Focus the amount input when QUANTITY cell is focused
        const cellRendererInstances = event.api.getCellRendererInstances({
          rowNodes: [rowNode],
          columns: [colId],
        });
        const component = cellRendererInstances?.[0] as MaturityAmountRenderer;
        const matAmtComp = component.maturityAmountEntry;
        if (matAmtComp?.focusInput) {
          matAmtComp.focusInput();
        }
      },
      context: {
        dealStatus: this.dealStatus,
      },
    };
  }

  clearAllOrdersModal: Modal;

  allOrdersUpdateConfirmModal: Modal;

  private gridApi: GridApi;

  private gridResizeTimeoutRef;

  private totalsRow;

  gridContainer: HTMLElement;

  splitOrderModal: SplitOrderModal;

  private heightObserver: ResizeObserver;

  private gridLayoutHeightRafId: number | null = null;

  private gridHeightCapped = false;

  @observable criteria: string;

  seriesChanged() {
    const seriesIdExpression = new ExpressionBuilder()
      .withField('SERIES_ID')
      .withValue(this.series.SERIES_ID)
      .build();
    this.setGridOptions(this.series.TAX_STATUS as TaxStatusEnum);
    if(this.gridApi) {
      this.gridApi.setColumnDefs(this.gridOptions.columnDefs);
    }
    this.criteria = new CriteriaBuilder()
      .withExpression(seriesIdExpression)
      .build();
  }

  getAllOrders(): MaturityOrder[] {
    if (!this.gridApi) {
      return [];
    }
    const allOrders: MaturityOrder[] = [];
    this.gridApi.getModel().forEachNode((rowNode) => {
      const data = rowNode.data as MaturityOrder;
      allOrders.push({
        ...data,
        SERIES_ID: data.SERIES_ID || this.series?.SERIES_ID,
        SERIES_CODE: data.SERIES_CODE || this.series?.SERIES_CODE,
      });
    });

    return allOrders;
  }

  openSplitOrderModal(order: MaturityOrder): void {
    const rowNode = this.gridApi?.getRowNode(order.MATURITY_ID);
    const latestOrder = (rowNode?.data ?? order) as MaturityOrder;
    const isExistingSplitOrder = isSplitMaturityGridOrder(latestOrder);
    if (!isExistingSplitOrder) {
      if (this.blockEditAndNotifyIfNeeded(latestOrder)) return;
    }
    this.splitOrderModal?.open({
      ...latestOrder,
      SERIES_ID: latestOrder.SERIES_ID || this.series?.SERIES_ID,
      SERIES_CODE: latestOrder.SERIES_CODE || this.series?.SERIES_CODE,
    });
  }

  /** Builds save payloads for split modal: normal amounts plus ALL-cleared submitted children. */
  private buildSplitSavePayload(
    sourceOrder: MaturityOrder,
    splitRows: SplitOrderRow[],
    splitOrders: MaturityOrder[],
  ) {
    const clearedSubmittedOrders = getSplitRowsForAllClearSave(sourceOrder, splitRows);
    const clearedOrderIds = new Set(
      clearedSubmittedOrders
        .map((order) => String(order.ORDER_ID ?? ''))
        .filter((orderId) => orderId !== ''),
    );
    return [
      ...splitOrders
        .filter((order) => !clearedOrderIds.has(String(order.ORDER_ID ?? '')))
        .map((order) => mapMaturityOrderForSubmission(order)),
      ...clearedSubmittedOrders.map((order) =>
        mapMaturityOrderForSubmission(order, { isClearingAllAmounts: true }),
      ),
    ];
  }

  async handleSaveSplit(
    e: CustomEvent<{
      sourceOrder: MaturityOrder;
      splitRows: SplitOrderRow[];
      baselineChildIds: string[];
      baselineChildren?: MaturityOrder[];
    }>,
  ): Promise<void> {
    const { sourceOrder, splitRows, baselineChildIds = [], baselineChildren = [] } = e.detail;
    const saveState = getSplitOrderSaveState(
      sourceOrder,
      splitRows,
      baselineChildIds,
      this.dealStatus,
    );
    if (!saveState.canSave) {
      return;
    }
    const splitOrders = getSplitRowsForSave(sourceOrder, splitRows);
    const collapseToLoneOrder = saveState.shouldCollapse;
    const keptOrderIds = new Set(splitOrders.map((order) => order.ORDER_ID).filter(Boolean));
    const baselineById = new Map(
      baselineChildren.map((order) => [String(order.ORDER_ID ?? ''), order]),
    );
    const removedOrderIds = baselineChildIds.filter((orderId) => {
      if (keptOrderIds.has(orderId)) return false;
      const baselineOrder = baselineById.get(orderId);
      return baselineOrder
        ? isSplitRowRemovable({
            rowId: '',
            rowType: 'split',
            splitAmount: '',
            order: baselineOrder,
          })
        : false;
    });

    if (removedOrderIds.length > 0) {
      const response = await this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
        DETAILS: {
          MUNI_ORDER_LIST: removedOrderIds.map((orderId) =>
            mapMaturityOrderForDeletion({ ...sourceOrder, ORDER_ID: orderId }),
          ),
        },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return;
      }
    }

    if (collapseToLoneOrder) {
      const collapseSavePayload = this.buildSplitSavePayload(sourceOrder, splitRows, splitOrders);
      if (collapseSavePayload.length > 0) {
        const response = await this.connect.commitEvent('EVENT_SAVE_MUNI_ORDERS', {
          DETAILS: {
            MUNI_ORDER_LIST: collapseSavePayload,
          },
        });
        if (response.ERROR?.length) {
          notifyApiError(response.ERROR[0]);
          return;
        }
      }

      const collapseResponse = await this.connect.commitEvent('EVENT_REMOVE_SPLIT_ORDER', {
        DETAILS: mapMaturityOrderForSubmission(sourceOrder),
      });
      if (collapseResponse.ERROR?.length) {
        notifyApiError(collapseResponse.ERROR[0]);
        return;
      }

      const rowNode = this.gridApi?.getRowNode(sourceOrder.MATURITY_ID);
      if (rowNode && splitOrders.length === 1) {
        const baseOrder = { ...rowNode.data, ...sourceOrder };
        rowNode.setData(buildCollapsedLoneOrderFromSplitChild(baseOrder, splitOrders[0]));
        this.changedRowIds.add(sourceOrder.MATURITY_ID);
        this.redrawRow(sourceOrder.MATURITY_ID);
      }

      this.splitOrderModal?.closeModal();
      this.updateTotalsRow();
      this.emitOrdersChanged();
      return;
    }

    const savePayload = this.buildSplitSavePayload(sourceOrder, splitRows, splitOrders);
    if (savePayload.length > 0) {
      const response = await this.connect.commitEvent('EVENT_SAVE_MUNI_ORDERS', {
        DETAILS: {
          MUNI_ORDER_LIST: savePayload,
        },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return;
      }
    }

    const rowNode = this.gridApi?.getRowNode(sourceOrder.MATURITY_ID);
    if (rowNode) {
      const hasSplit = saveState.shouldPersistAsSplit;
      const baseOrder = { ...rowNode.data, ...sourceOrder };
      const updatedOrder: MaturityOrder = hasSplit
        ? {
          ...getSplitConditionsRollupOrder(baseOrder, splitRows),
          HAS_SPLIT: true,
          IS_SPLIT_MATURITY: true,
          IS_SPLIT: false,
          QUANTITY: getSplitParentQuantityTotal(splitRows),
          IS_AUTO_RESET_ORDER_AMOUNT: false,
        }
        : buildGridRowAfterSplitChildrenCleared(baseOrder);
      rowNode.setData(updatedOrder);
      this.changedRowIds.add(sourceOrder.MATURITY_ID);
      this.redrawRow(sourceOrder.MATURITY_ID);
    }

    this.splitOrderModal?.closeModal();
    this.updateTotalsRow();
    this.emitOrdersChanged();
  }

  async handleRemoveSplit(e: CustomEvent<{ maturityId: string }>): Promise<void> {
    const maturityId = e.detail?.maturityId;
    if (!maturityId) return;
    const rowNode = this.gridApi?.getRowNode(maturityId);
    const sourceOrder = rowNode?.data as MaturityOrder;
    if (!sourceOrder) return;

    const children = await fetchBondOrderChildren(this.connect, maturityId);
    if (children.length !== 1) return;

    const collapsed = await this.collapseSplitParentToLoneOrder(sourceOrder, children[0], true);
    if (!collapsed || !rowNode) return;

    rowNode.setData(collapsed);
    this.changedRowIds.add(maturityId);
    this.redrawRow(maturityId);
    this.splitOrderModal?.closeModal();
    this.updateTotalsRow();
    this.emitOrdersChanged();
  }

  /** Collapse split maturity to lone order after bulk clear or explicit remove-split. */
  private async collapseSplitParentToLoneOrder(
    gridParent: MaturityOrder,
    child: MaturityOrder,
    notifyOnError: boolean,
  ): Promise<MaturityOrder | null> {
    const response = await this.connect.commitEvent('EVENT_REMOVE_SPLIT_ORDER', {
      DETAILS: mapMaturityOrderForSubmission(gridParent),
    });
    if (response.ERROR?.length) {
      if (notifyOnError) {
        notifyApiError(response.ERROR[0]);
      }
      return null;
    }
    return buildCollapsedLoneOrderFromSplitChild(gridParent, child);
  }

  /**
   * Re-sync split parent grid rows after Clear All / Global All erase.
   * Collapses when one child remains; clears split flags when none remain.
   */
  private async refreshSplitParentsAfterBulkChildMutation(
    splitParents: MaturityOrder[],
  ): Promise<MaturityOrder[]> {
    const updates: MaturityOrder[] = [];
    for (const parent of splitParents) {
      const rowNode = this.gridApi?.getRowNode(parent.MATURITY_ID);
      const gridParent = (rowNode?.data ?? parent) as MaturityOrder;
      const children = await fetchBondOrderChildren(this.connect, parent.MATURITY_ID);
      const mergedChildren = children.map((child) => mergeChildWithGridParent(gridParent, child));

      if (children.length === 0) {
        updates.push(buildGridRowAfterSplitChildrenCleared(gridParent));
        continue;
      }

      if (children.length === 1) {
        const collapsed = await this.collapseSplitParentToLoneOrder(
          gridParent,
          mergedChildren[0],
          false,
        );
        if (collapsed) {
          updates.push(collapsed);
          continue;
        }
      }

      updates.push(buildSplitParentRollupFromChildren(gridParent, mergedChildren));
    }
    return updates;
  }

  async handleSplitLineChanged(e?: CustomEvent<{ maturityId?: string }>): Promise<void> {
    const maturityId = e?.detail?.maturityId;
    if (maturityId && this.gridApi) {
      const rowNode = this.gridApi.getRowNode(maturityId);
      const parent = rowNode?.data as MaturityOrder | undefined;
      if (parent) {
        const children = await fetchBondOrderChildren(this.connect, maturityId);
        const updatedOrder = buildSplitParentRollupFromChildren(
          parent,
          children.map((child) => mergeChildWithGridParent(parent, child)),
        );
        rowNode.setData(updatedOrder);
        this.changedRowIds.add(maturityId);
        this.redrawRow(maturityId);
      }
    }
    this.updateTotalsRow();
    this.emitOrdersChanged();
  }

  handleGridReady(e: CustomEvent): void {
    this.gridApi = e.detail.event.api;
    if (this.totalsRow) {
      this.gridApi.setPinnedBottomRowData([this.totalsRow]);
    }
    this.resizeGrid();
    this.updateGridContainerHeight();
    window.addEventListener('resize', () => {
      this.resizeGrid();
      this.resetGridHeightCap();
      this.updateGridContainerHeight();
    });

    const parent = this.gridContainer?.parentElement;
    if (parent) {
      this.heightObserver = new ResizeObserver(() => this.updateGridContainerHeight());
      this.heightObserver.observe(parent);
    }
  }

  handleOrderConditionsChanged(e: CustomEvent): void {
    const maturityOrders = this.getAllOrders();
    const orderIndex = maturityOrders.findIndex((o) => o.MATURITY_ID === e.detail.MATURITY_ID);
    if (orderIndex !== -1) {
      const previousOrder = maturityOrders[orderIndex];
      const order = e.detail as MaturityOrder;
      maturityOrders[orderIndex] = order;
      this.gridApi.getRowNode(order.MATURITY_ID).setData(order);
      this.changedRowIds.add(order.MATURITY_ID);
      this.redrawRow(order.MATURITY_ID);

      if (this.conditionsActive && this.orderEditConditions?.MATURITY_ID === order.MATURITY_ID) {
        this.orderEditConditions = order;
      }

      // Always persist conditions changes; maximum par/multiples errors are shown in the modal only.
      this.postOrderChange(order, previousOrder);
    }
  }

  closeConditionsPopup(): void {
    this.conditionsActive = false;
    this.orderEditConditions = null;
  }

  private handleSinkingFundCellMouseOver(event: CellMouseOverEvent<MaturityOrder>): void {
    if (event.node.isRowPinned() || !event.data) {
      return;
    }
    const mouseEvent = event.event as MouseEvent | undefined;
    if (!mouseEvent) {
      return;
    }
    if (isZeroCouponMaturity(event.data.SECURITY_TYPE) && hasZeroCouponPrincipalAmount(event.data)) {
      this.principalAmountValue = String(event.data.MATURITY_PRESENT_VALUE ?? '').trim();
      this.principalAmountHighlighted = shouldHighlightPrincipalAmountOnHover(
        event.data,
        this.dealStatus,
      );
      this.principalAmountAnchorTop = mouseEvent.clientY + 12;
      this.principalAmountAnchorLeft = mouseEvent.clientX + 12;
      this.principalAmountModalVisible = true;
      return;
    }
    if (!isTermBondMaturity(event.data.SINKING_FUND_SCHEDULE)) {
      return;
    }
    this.clearSinkingFundHideTimeout();
    this.sinkingFundScheduleJson = event.data.SINKING_FUND_SCHEDULE ?? null;
    this.sinkingFundPanelStyle = computeSinkingFundPanelLayout(mouseEvent);
    this.sinkingFundPanelHovered = false;
    this.sinkingFundModalVisible = true;
  }

  handleSinkingFundPanelMouseEnter(): void {
    this.clearSinkingFundHideTimeout();
    this.sinkingFundPanelHovered = true;
  }

  handleSinkingFundPanelMouseLeave(): void {
    this.sinkingFundPanelHovered = false;
    this.sinkingFundModalVisible = false;
    this.sinkingFundScheduleJson = null;
  }

  private clearSinkingFundHideTimeout(): void {
    if (this.sinkingFundHideTimeout !== null) {
      clearTimeout(this.sinkingFundHideTimeout);
      this.sinkingFundHideTimeout = null;
    }
  }

  private handleSinkingFundCellMouseOut(): void {
    if (this.sinkingFundModalVisible) {
      this.clearSinkingFundHideTimeout();
      this.sinkingFundHideTimeout = window.setTimeout(() => {
        this.sinkingFundHideTimeout = null;
        if (!this.sinkingFundPanelHovered) {
          this.sinkingFundModalVisible = false;
          this.sinkingFundScheduleJson = null;
        }
      }, 150);
    }
    this.principalAmountModalVisible = false;
    this.principalAmountValue = null;
    this.principalAmountHighlighted = false;
  }

  /**
   * Restore orders to the last SUBMITTED/ACCEPTED audit snapshot (persists SUBMITTED full/partial par).
   * Used by cancel/revert, global/per-row All restore, and global All clear for submitted rows.
   */
  private async resetOrdersToLastSubmittedSnapshot(orders: MaturityOrder[]): Promise<boolean> {
    const toReset = orders.filter((o) => o.ORDER_ID);
    if (!toReset.length) return true;
    try {
      const response = await this.connect.commitEvent('EVENT_RESET_ORDERS_LIST', {
        DETAILS: {
          RESET_ORDERS_LIST: toReset.map((o) => mapMaturityOrderForSubmission(o)),
        },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        return false;
      }
      toReset.forEach((o) => this.changedRowIds.add(o.MATURITY_ID));
      this.updateTotalsRow();
      this.emitOrdersChanged();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  /** — Reset SUBMITTED_AND_DRAFT or submitted-track edits to server snapshot; clear DRAFT. */
  async revertOrder(orderToRevert: MaturityOrder): Promise<void> {
    const ok = await this.resetOrdersToLastSubmittedSnapshot([orderToRevert]);
    if (ok && this.gridApi) {
      this.redrawRow(orderToRevert.MATURITY_ID);
    }
  }

  /** — Show clear-all modal with dynamic copy. Split parents expand to child lines; do not gate on parent rollup eligibility. */
  private async toggleClearAllModal(): Promise<void> {
    const maturityOrders = this.getAllOrders();

    const { resetOrders, deleteOrders } = await resolveClearAllDraftBuckets(
      this.connect,
      maturityOrders,
      {
        isInRepriceWindow: this.isInRepriceWindow(),
        dealStatus: this.dealStatus,
      },
    );

    if (resetOrders.length === 0 && deleteOrders.length === 0) {
      if (this.isInRepriceWindow()) {
        notifyOrderBlocked(OrderBlockReason.CLEAR_ALL_REPRICE_NO_RESET);
      }
      return;
    }

    const messages = getClearAllMessages([...resetOrders, ...deleteOrders]);

    this.clearAllModalHeader = messages.clearAllModalHeader;
    this.clearAllModalBody = messages.clearAllModalBody;
    this.clearAllModalBodySecondLine = messages.clearAllModalBodySecondLine;
    this.clearAllModalButtonText = messages.clearAllModalButtonText;

    this.clearAllOrdersModal.show();
  }

  cancelClearAllOrders(): void {
    this.clearAllOrdersModal.close();
  }

  cancelUpdateAllOrders(): void {
    this.allOrdersUpdateConfirmModal.close();
  }

  /** — Reset SUBMITTED_AND_DRAFT; delete DRAFT; refresh grid. In reprice window only reset reconfirm-eligible orders. Cleanup; not gated by the order window — mirrors cancel from deal detail. */
  async clearAllDraftOrders(): Promise<void> {
    const maturityOrders = this.getAllOrders();
    try {
      const { resetOrders, deleteOrders, splitParents } = await resolveClearAllDraftBuckets(
        this.connect,
        maturityOrders,
        {
          isInRepriceWindow: this.isInRepriceWindow(),
          dealStatus: this.dealStatus,
        },
      );

      if (resetOrders.length === 0 && deleteOrders.length === 0) {
        this.clearAllOrdersModal.close();
        return;
      }

      if (this.isInRepriceWindow() && resetOrders.length === 0) {
        this.clearAllOrdersModal.close();
        notifyOrderBlocked(OrderBlockReason.CLEAR_ALL_REPRICE_NO_RESET);
        return;
      }

      if (resetOrders.length > 0) {
        const resetResponse = await this.connect.commitEvent('EVENT_RESET_ORDERS_LIST', {
          DETAILS: {
            RESET_ORDERS_LIST: resetOrders.map((o) => mapMaturityOrderForSubmission(o)),
          },
        });
        if (resetResponse.ERROR?.length) {
          notifyApiError(resetResponse.ERROR[0]);
          return;
        }
      }

      if (deleteOrders.length > 0) {
        const deleteResponse = await this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
          DETAILS: {
            MUNI_ORDER_LIST: deleteOrders.map(mapMaturityOrderForDeletion),
          },
        });
        if (deleteResponse.ERROR?.length) {
          notifyApiError(deleteResponse.ERROR[0]);
          return;
        }
      }

      if (splitParents.length > 0 && this.gridApi) {
        const refreshedSplitParentRollups = await this.refreshSplitParentsAfterBulkChildMutation(
          splitParents,
        );
        if (refreshedSplitParentRollups.length) {
          this.updateChangedRows(refreshedSplitParentRollups);
        }
        this.scheduleSplitParentRollupRefreshFromChildren();
      }

      const affectedMaturityIds = new Set([
        ...resetOrders.map((order) => order.MATURITY_ID),
        ...deleteOrders.map((order) => order.MATURITY_ID),
        ...splitParents.map((parent) => parent.MATURITY_ID),
      ]);
      if (affectedMaturityIds.size > 0 && this.gridApi) {
        affectedMaturityIds.forEach((maturityId) => {
          const rowNode = this.gridApi.getRowNode(maturityId);
          if (rowNode) {
            this.changedRowIds.add(maturityId);
            this.redrawRow(maturityId);
          }
        });
      }

      this.emitOrdersChanged();
      this.updateTotalsRow();
      this.clearAllOrdersModal.close();
    } catch (e) {
      console.error(e);
    }
  }

  /** — Apply-all confirmed: set eligible orders to maturity amount (reconfirm-aware). */
  confirmUpdateAllOrders(): void {
    this.toggleAutoResetForAllOrders(this.getAllOrders(), true);
    this.allOrdersUpdateConfirmModal.close();
  }

  /** — Update row data and notify parent (blur-safe). */
  private setRowEditForField(
    MATURITY_ID: string,
    order: MaturityOrder,
    saveOptions?: { isClearingAllAmounts?: boolean },
  ) {
    const rowNode = this.gridApi.getRowNode(MATURITY_ID);
    const previousOrder = rowNode.data as MaturityOrder;
    rowNode.setData(order);
    this.changedRowIds.add(MATURITY_ID);
    this.postOrderChange(order, previousOrder, saveOptions);
  }

  private redrawRow(rowNodeId: string) {
    this.gridApi.redrawRows({
      rowNodes: [this.gridApi.getRowNode(rowNodeId)],
    });
  }

  public updateTotalsRow(): void {
    const maturityOrders = this.getAllOrders();
    const isSingleSeries = !this.bondSeriesList || this.bondSeriesList.length <= 1;
    this.totalsRow = getTotalsRowData(maturityOrders, isSingleSeries);
    if (this.gridApi && maturityOrders) {
      setTimeout(() => {
        this.gridApi.setPinnedBottomRowData([this.totalsRow]);
        this.updateGridContainerHeight();
      }, 1);
    }
  }

  private resetGridHeightCap(): void {
    this.gridHeightCapped = false;
    this.gridContainer?.classList.remove('deal-grid-container--capped');
  }

  private applyCappedGridLayout(viewportBottom: number): void {
    if (!this.gridApi || !this.gridContainer) return;

    const gridTop = this.gridContainer.getBoundingClientRect().top;
    this.gridHeightCapped = true;
    this.gridContainer.classList.add('deal-grid-container--capped');
    this.gridApi.setDomLayout('normal');
    this.gridContainer.style.height = `${cappedGridHeightPx(gridTop, viewportBottom)}px`;
  }

  private updateGridContainerHeight(): void {
    if (!this.gridApi || !this.gridContainer) return;

    const parent = this.gridContainer.parentElement;
    if (!parent) return;

    // Hidden tab panels (display:none) have no layout height; skip and recalc when visible.
    if (parent.clientHeight === 0) return;

    if (this.gridLayoutHeightRafId !== null) {
      cancelAnimationFrame(this.gridLayoutHeightRafId);
      this.gridLayoutHeightRafId = null;
    }

    const viewportBottom = getGridViewportBottomPx(this.gridContainer);
    const gridTop = this.gridContainer.getBoundingClientRect().top;
    const rowCount = this.gridApi.getDisplayedRowCount();

    if (
      this.gridHeightCapped ||
      gridWouldOverflowViewport(gridTop, viewportBottom, rowCount)
    ) {
      this.applyCappedGridLayout(viewportBottom);
      return;
    }

    // Pass 1: let AG Grid size to true content (includes horizontal scrollbar, borders, pinned row).
    this.gridApi.setDomLayout('autoHeight');
    this.gridContainer.style.height = 'auto';

    const measureAndApply = () => {
      this.gridLayoutHeightRafId = null;
      if (!this.gridApi || !this.gridContainer) return;

      const naturalBottom = this.gridContainer.getBoundingClientRect().bottom;

      if (gridFitsViewport(naturalBottom, viewportBottom)) {
        return;
      }

      this.applyCappedGridLayout(viewportBottom);
    };

    this.gridLayoutHeightRafId = requestAnimationFrame(() => {
      this.gridLayoutHeightRafId = requestAnimationFrame(measureAndApply);
    });
  }

  private emitOrdersChanged(): void {
      if (!this.gridApi || !this.series) {
        return;
      }
      const orders: MaturityOrder[] = this.getAllOrders();
      this.$emit('orders-changed', { seriesId: this.series.SERIES_ID, orders });
    }

  private async postOrderChange(
    order: MaturityOrder,
    previousOrder?: MaturityOrder,
    saveOptions?: { isClearingAllAmounts?: boolean },
  ): Promise<void> {
    try {
      if (shouldDeleteDraftOrder(order, previousOrder)) {
        const response = await this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
          DETAILS: {
            MUNI_ORDER_LIST: [mapMaturityOrderForDeletion(order)],
          },
        });
        if (response.ERROR?.length) {
          notifyApiError(response.ERROR[0]);
          if (previousOrder && this.gridApi) this.revertRowToOrder(order.MATURITY_ID, previousOrder);
        } else {
          this.updateTotalsRow();
          this.emitOrdersChanged();
        }
      } else {
        const response = await this.connect.commitEvent('EVENT_SAVE_MUNI_ORDER', {
          DETAILS: mapMaturityOrderForSubmission(order, saveOptions),
        });
        if (response.ERROR?.length) {
          notifyApiError(response.ERROR[0]);
          console.error('error saving order', response.ERROR);
          if (previousOrder && this.gridApi) this.revertRowToOrder(order.MATURITY_ID, previousOrder);
        } else {
          this.updateTotalsRow();
          this.emitOrdersChanged();
        }
      }
    } catch (e) {
      console.error('error submitting order', e);
      if (previousOrder && this.gridApi) this.revertRowToOrder(order.MATURITY_ID, previousOrder);
    }
  }

  /** Revert a row to previous order data so invalid values are not left in the grid after nack. */
  private revertRowToOrder(MATURITY_ID: string, previousOrder: MaturityOrder): void {
    const node = this.gridApi.getRowNode(MATURITY_ID);
    if (!node) return;
    node.setData(previousOrder);
    this.changedRowIds.add(MATURITY_ID);
    this.redrawRow(MATURITY_ID);
    this.updateTotalsRow();
    if (this.orderEditConditions?.MATURITY_ID === MATURITY_ID) {
      this.orderEditConditions = previousOrder;
    }
  }

  private async postBulkOrderChange(
    orders: MaturityOrder[],
    skipUiUpdate = false,
    options?: { isClearingAllAmounts?: boolean },
  ): Promise<void> {
    try {
      const response = await this.connect.commitEvent('EVENT_SAVE_MUNI_ORDERS', {
        DETAILS: {
          MUNI_ORDER_LIST: orders.map((o) => mapMaturityOrderForSubmission(o, options)),
        },
      });
      if (response.ERROR?.length) {
        notifyApiError(response.ERROR[0]);
        console.error('error submitting order', response.ERROR);
      } else if (!skipUiUpdate) {
        this.updateTotalsRow();
        this.emitOrdersChanged();
      }
    } catch (e) {
      console.error('error submitting order', e);
    }
  }

  private resizeGrid(): void {
    clearTimeout(this.gridResizeTimeoutRef);
    this.gridResizeTimeoutRef = setTimeout(() => {
      this.updateGridContainerHeight();
    }, 200);
  }

  private async refreshSplitParentRollupsFromChildren(options?: {
    allSplitParents?: boolean;
  }): Promise<void> {
    if (!this.gridApi || this.splitParentRollupRefreshInFlight) return;
    const gridOrders = this.getAllOrders();
    const splitParents = gridOrders.filter(isSplitMaturityGridOrder);
    if (splitParents.length === 0) return;

    const shouldRefreshAll =
      options?.allSplitParents === true ||
      this.splitParentHasInTransitChildren ||
      this.splitParentStatusPollInterval != null ||
      Date.now() < this.splitParentPollGraceUntil;
    const parentsToRefresh = shouldRefreshAll ? splitParents : splitParents.filter(isOrderInTransit);
    if (parentsToRefresh.length === 0) return;

    this.splitParentRollupRefreshInFlight = true;
    try {
      const { updates, hasInTransitChildren } = await resolveSplitParentRollupsForGrid(
        this.connect,
        gridOrders,
        { parentsOnly: parentsToRefresh },
      );
      this.splitParentHasInTransitChildren = hasInTransitChildren;
      if (updates.length > 0) {
        this.updateChangedRows(updates);
        const rowNodes = updates
          .map((order) => this.gridApi.getRowNode(order.MATURITY_ID))
          .filter(Boolean);
        if (rowNodes.length > 0) {
          this.gridApi.refreshCells({
            rowNodes,
            columns: ['ORDER_STATUS'],
            force: true,
          });
        }
        this.updateTotalsRow();
        this.emitOrdersChanged();
      }
    } finally {
      this.splitParentRollupRefreshInFlight = false;
      this.syncSplitParentStatusPolling();
    }
  }

  /** Start transit hold before submit completes so streamed anchor rows stay on PENDING. */
  beginSplitParentTransitHold(): void {
    this.splitParentPollGraceUntil = Date.now() + DealSeries.SPLIT_PARENT_POLL_GRACE_MS;
    this.syncSplitParentStatusPolling();
    void this.refreshSplitParentRollupsFromChildren({ allSplitParents: true });
  }

  /**
   * When the dataserver streams a split parent from PENDING to SUBMITTED_AND_DRAFT during
   * submit, start the transit hold without coupling to deal-submit-preview.
   */
  private beginSplitParentTransitHoldIfStreamedFromPending(): void {
    for (const order of this.getAllOrders()) {
      if (!isSplitMaturityGridOrder(order)) continue;
      if (order.ORDER_STATUS !== OrderStatusEnum.SUBMITTED_AND_DRAFT) continue;

      const previousStatus = this.lastOrderStatusByMaturityId.get(order.MATURITY_ID);
      if (
        previousStatus === OrderStatusEnum.PENDING ||
        previousStatus === OrderStatusEnum.PENDING_CANCELLED
      ) {
        this.splitParentPollGraceUntil = Date.now() + DealSeries.SPLIT_PARENT_POLL_GRACE_MS;
        return;
      }
    }
  }

  /** Re-fetch split child lines and refresh parent rollup rows (e.g. after submit). */
  refreshSplitParentRollups(): void {
    this.beginSplitParentTransitHold();
  }

  private isSplitParentTransitHoldActive(): boolean {
    return (
      this.splitParentHasInTransitChildren ||
      this.splitParentStatusPollInterval != null ||
      Date.now() < this.splitParentPollGraceUntil
    );
  }

  private coerceSplitParentDisplayStatusesDuringTransitHold(api: GridApi): void {
    api.forEachNode((node) => {
      const order = node.data as MaturityOrder;
      const coerced = coerceSplitParentDisplayStatusDuringTransitHold(
        order,
        true,
      );
      if (coerced.ORDER_STATUS !== order.ORDER_STATUS) {
        node.setData(coerced);
        this.changedRowIds.add(order.MATURITY_ID);
      }
    });
  }

  private scheduleSplitParentRollupRefreshFromChildren(): void {
    if (this.splitParentRollupRefreshDebounceTimeout != null) {
      clearTimeout(this.splitParentRollupRefreshDebounceTimeout);
    }
    this.splitParentRollupRefreshDebounceTimeout = setTimeout(() => {
      this.splitParentRollupRefreshDebounceTimeout = null;
      void this.refreshSplitParentRollupsFromChildren({ allSplitParents: true });
    }, DealSeries.SPLIT_PARENT_ROLLUP_REFRESH_DEBOUNCE_MS);
  }

  private hasInTransitSplitParent(gridOrders: MaturityOrder[]): boolean {
    return gridOrders.some(
      (order) => isSplitMaturityGridOrder(order) && isOrderInTransit(order),
    );
  }

  private startSplitParentStatusPolling(): void {
    if (this.splitParentStatusPollInterval != null) return;
    this.splitParentStatusPollInterval = setInterval(() => {
      void this.refreshSplitParentRollupsFromChildren();
    }, DealSeries.SPLIT_PARENT_STATUS_POLL_MS);
    void this.refreshSplitParentRollupsFromChildren();
  }

  private stopSplitParentStatusPolling(): void {
    if (this.splitParentStatusPollInterval == null) return;
    clearInterval(this.splitParentStatusPollInterval);
    this.splitParentStatusPollInterval = null;
  }

  private syncSplitParentStatusPolling(): void {
    if (!this.gridApi) return;
    const gridOrders = this.getAllOrders();
    const shouldPoll =
      this.hasInTransitSplitParent(gridOrders) ||
      this.splitParentHasInTransitChildren ||
      Date.now() < this.splitParentPollGraceUntil;
    if (shouldPoll) {
      this.startSplitParentStatusPolling();
    } else {
      this.stopSplitParentStatusPolling();
    }
  }

  /** — Batch row updates and mark for redraw. */
  private updateChangedRows(updatedOrders: MaturityOrder[]): void {
    if (!this.gridApi || !updatedOrders.length) return;
    updatedOrders.forEach((row) => {
      const node = this.gridApi.getRowNode(row.MATURITY_ID);
      if (node) {
        node.setData(row);
        this.changedRowIds.add(row.MATURITY_ID);
      }
    });
  }

  /** — Apply: set all to maturity amount (incl. new drafts). Clear: zero out or delete drafts. */
  private async toggleAutoResetForAllOrders(
    orders: MaturityOrder[],
    applyAmounts?: boolean,
    options?: { scopeToPassedOrders?: boolean },
  ): Promise<void> {
    const shouldApply = applyAmounts === true;
    if (shouldApply && !orders?.length) return;

    const gridOrders = (shouldApply || options?.scopeToPassedOrders ? orders : this.getAllOrders()).map(
      (o) => this.gridApi.getRowNode(o.MATURITY_ID)?.data ?? o,
    ) as MaturityOrder[];
    const shouldClear = applyAmounts === false;

    if (shouldApply) {
      const eligible = getOrdersEligibleForGlobalAll(gridOrders, this.dealStatus);
      if (eligible.length === 0) return;

      const eligibleIds = new Set(eligible.map((o) => o.MATURITY_ID));
      const fillToPar = (o: MaturityOrder): MaturityOrder => ({
        ...o,
        IS_AUTO_RESET_ORDER_AMOUNT: true,
        QUANTITY: getOrderAmountCap(o),
        IS_ZERO_CANCEL_INTENT: false,
      });
      const ordersToSave: MaturityOrder[] = [];
      const gridRowsToUpdate: MaturityOrder[] = [];

      for (const order of eligible) {
        if (isSplitMaturityGridOrder(order)) {
          const children = await fetchBondOrderChildren(this.connect, order.MATURITY_ID);
          if (children.length === 0) continue;
          const filledChildren = children.map((child) => fillToPar(mergeChildWithGridParent(order, child)));
          ordersToSave.push(...filledChildren);
          gridRowsToUpdate.push(buildSplitParentRollupFromChildren(order, filledChildren));
          continue;
        }
        const filled = fillToPar(order);
        ordersToSave.push(filled);
        gridRowsToUpdate.push(filled);
      }

      // Outside reconfirm: only create new DRAFT rows for empty maturities — never downgrade submitted track.
      const draftRowsForGlobalAll = this.isInRepriceWindow()
        ? []
        : gridOrders.filter(
            (m) =>
              getOrderAmountCap(m) &&
              !eligibleIds.has(m.MATURITY_ID) &&
              !isSplitMaturityGridOrder(m) &&
              isOrderModifiable(m) &&
              !isSubmittedFullParCapOnRecord(m) &&
              !isOrderInstructedForReconfirm(m) &&
              (!m.ORDER_ID || m.ORDER_STATUS === OrderStatusEnum.DRAFT || !m.ORDER_STATUS),
          );
      ordersToSave.push(
        ...draftRowsForGlobalAll.map((m) => ({
          ...fillToPar(m),
          ORDER_ID: '',
          ORDER_STATUS: OrderStatusEnum.DRAFT,
          SERIES_ID: m.SERIES_ID || this.series?.SERIES_ID,
          SERIES_CODE: m.SERIES_CODE || this.series?.SERIES_CODE,
        })),
      );
      gridRowsToUpdate.push(
        ...draftRowsForGlobalAll.map((m) => ({
          ...fillToPar(m),
          ORDER_ID: '',
          ORDER_STATUS: OrderStatusEnum.DRAFT,
          SERIES_ID: m.SERIES_ID || this.series?.SERIES_ID,
          SERIES_CODE: m.SERIES_CODE || this.series?.SERIES_CODE,
        })),
      );
      if (gridRowsToUpdate.length) {
        this.updateChangedRows(gridRowsToUpdate);
        this.gridApi?.redrawRows();
      }
      if (ordersToSave.length) {
        await this.postBulkOrderChange(ordersToSave);
      }
    } else if (shouldClear) {
      // Rows with submitted quantity on record are reset to the last SUBMITTED audit snapshot
      // (persisted). Other clearable rows are wiped on the backend as usual.
      const eligibleClearIds = this.isInRepriceWindow()
        ? new Set(getOrdersEligibleForGlobalAll(gridOrders, this.dealStatus).map((o) => o.MATURITY_ID))
        : null;
      const revertTargets: MaturityOrder[] = [];
      const persistTargets: MaturityOrder[] = [];
      const splitParentsForClear: MaturityOrder[] = [];
      gridOrders.forEach((o) => {
        if (eligibleClearIds != null && !eligibleClearIds.has(o.MATURITY_ID)) return;
        if (o.ORDER_STATUS === OrderStatusEnum.PENDING || o.ORDER_STATUS === OrderStatusEnum.PENDING_CANCELLED) return;
        if (isSplitMaturityGridOrder(o)) {
          splitParentsForClear.push(o);
          return;
        }
        if (hasSubmittedAmountOnRecord(o)) {
          revertTargets.push(o);
          return;
        }
        if (!!o.IS_AUTO_RESET_ORDER_AMOUNT || !!o.IS_ZERO_CANCEL_INTENT || hasOrderAmountApplied(o)) {
          persistTargets.push(o);
        }
      });

      for (const parent of splitParentsForClear) {
        const children = await fetchBondOrderChildren(this.connect, parent.MATURITY_ID);
        for (const child of children) {
          const merged = mergeChildWithGridParent(parent, child);
          if (hasSubmittedAmountOnRecord(merged)) {
            revertTargets.push(merged);
            continue;
          }
          if (
            !!merged.IS_AUTO_RESET_ORDER_AMOUNT ||
            !!merged.IS_ZERO_CANCEL_INTENT ||
            hasOrderAmountApplied(merged)
          ) {
            persistTargets.push(merged);
          }
        }
      }

      const toDelete = persistTargets.filter((o) =>
        shouldDeleteDraftOrder({ ...o, ...getOrderPatchAfterAllClear(o) }, o),
      );
      const toSave = persistTargets
        .filter((o) => !toDelete.includes(o))
        .map((o) => ({ ...o, ...getOrderPatchAfterAllClear(o) }));
      const splitParentRollupUpdates: MaturityOrder[] = [];
      for (const parent of splitParentsForClear) {
        const children = await fetchBondOrderChildren(this.connect, parent.MATURITY_ID);
        const rollupChildren = children
          .map((child) => mergeChildWithGridParent(parent, child))
          .map((merged) => {
            const saved = toSave.find((order) => order.ORDER_ID === merged.ORDER_ID);
            if (saved) return saved;
            const deleted = toDelete.find((order) => order.ORDER_ID === merged.ORDER_ID);
            if (deleted) return { ...merged, ...getOrderPatchAfterAllClear(deleted) };
            if (hasSubmittedAmountOnRecord(merged)) {
              return { ...merged, ...getSubmittedAmountRevertPatch(merged) };
            }
            return merged;
          })
          .filter((merged) => !toDelete.some((order) => order.ORDER_ID === merged.ORDER_ID));
        splitParentRollupUpdates.push(buildSplitParentRollupFromChildren(parent, rollupChildren));
      }
      const gridRowsToClear = [
        ...toSave.filter((order) => !isSplitChildOrder(order)),
        ...splitParentRollupUpdates,
      ];
      if (gridRowsToClear.length) {
        this.updateChangedRows(gridRowsToClear);
        this.gridApi?.redrawRows();
      }
      await Promise.all([
        revertTargets.length && this.resetOrdersToLastSubmittedSnapshot(revertTargets),
        toDelete.length &&
          this.connect.commitEvent('EVENT_DELETE_MUNI_ORDER', {
            DETAILS: { MUNI_ORDER_LIST: toDelete.map(mapMaturityOrderForDeletion) },
          }),
        toSave.length && this.postBulkOrderChange(toSave, false, { isClearingAllAmounts: true }),
      ].filter(Boolean));
      if (splitParentsForClear.length) {
        const refreshedSplitParentRollups = await this.refreshSplitParentsAfterBulkChildMutation(
          splitParentsForClear,
        );
        if (refreshedSplitParentRollups.length) {
          this.updateChangedRows(refreshedSplitParentRollups);
          this.gridApi?.redrawRows();
        }
        // Open the reconciliation window so onRowDataUpdated keeps recomputing the parent rollup
        // from children, discarding any stale ALL_MATURITY_ORDERS anchor QUANTITY streamed after clear.
        this.beginSplitParentTransitHold();
      }
      this.updateTotalsRow();
      this.emitOrdersChanged();
    }
  }

  handleDocumentClick(e: Event): void {
    const path = e.composedPath();
    if (this.conditionsPopup && !path.includes(this.conditionsPopup)) {
      this.closeConditionsPopup();
    }
  }

  private handleDocumentClickBound: ((e) => void) | null = null;

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClickBound);
    this.stopSplitParentStatusPolling();
    if (this.splitParentRollupRefreshDebounceTimeout != null) {
      clearTimeout(this.splitParentRollupRefreshDebounceTimeout);
      this.splitParentRollupRefreshDebounceTimeout = null;
    }
    this.lastOrderStatusByMaturityId.clear();
    if (this.gridLayoutHeightRafId !== null) {
      cancelAnimationFrame(this.gridLayoutHeightRafId);
      this.gridLayoutHeightRafId = null;
    }
    this.gridHeightCapped = false;
    this.heightObserver?.disconnect();
    super.disconnectedCallback();
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.series?.TAX_STATUS) {
      this.setGridOptions(this.series.TAX_STATUS as TaxStatusEnum);
    }
    this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.handleDocumentClickBound);
  }
}
